/**
 * Orbis's privacy policy and terms (f6-18), as data so both pages render the same way.
 * Decided 2026-10-31: the responsible party is "Orbis" until the company is formed (then
 * its legal name and cédula jurídica), privacy requests go to privacidad@, and a company's
 * data is kept 60 days after it leaves. Every statement here must match what Orbis does:
 * change the code and this text together. A lawyer reviews it once the company exists.
 */

export const LEGAL_UPDATED = "1 de noviembre de 2026";
export const PRIVACY_EMAIL = "privacidad@orbiscrm.app";
export const SALES_EMAIL = "ventas@orbiscrm.app";

export interface LegalSection {
  title: string;
  paragraphs?: string[];
  items?: string[];
  after?: string[];
}

export const PRIVACY: LegalSection[] = [
  {
    title: "Quiénes somos",
    paragraphs: [
      "Orbis es un servicio para que las empresas atiendan su WhatsApp con su equipo y con Lumi, una inteligencia artificial que responde con la información que cada empresa carga. Esta política explica qué datos tratamos, para qué, con quién los compartimos y cómo puedes ejercer tus derechos.",
      `Para cualquier pregunta sobre privacidad, escríbenos a ${PRIVACY_EMAIL}.`,
    ],
  },
  {
    title: "Dos tipos de datos, dos responsables",
    items: [
      "Los datos de los clientes de cada empresa (sus contactos, sus conversaciones de WhatsApp, sus casos y cotizaciones) pertenecen a esa empresa. Ella decide qué guarda y para qué; Orbis los trata solo por encargo suyo y para darle el servicio.",
      "Los datos de las cuentas del panel (las personas de cada empresa que usan Orbis) y de la relación comercial con cada empresa los trata Orbis como responsable.",
    ],
    after: [
      "Si eres cliente de una empresa que usa Orbis y quieres ver, corregir o borrar tus datos, lo más rápido es pedírselo a esa empresa. Si nos escribes a nosotros, le trasladamos tu pedido.",
    ],
  },
  {
    title: "Qué datos tratamos",
    items: [
      "Cuentas: nombre, correo, contraseña (guardada solo como un resumen cifrado que no se puede revertir), código de verificación en dos pasos, sesiones abiertas, y la dirección IP y el navegador con que se entra, para avisarte de cada inicio de sesión y frenar intentos sospechosos.",
      "La empresa: su nombre, datos de contacto, horario, catálogo, documentos de conocimiento, imágenes, cotizaciones y la configuración de Lumi.",
      "WhatsApp: el número y el nombre de perfil de cada contacto, los mensajes y archivos que se envían y reciben, su estado de entrega, y los datos que el equipo agrega (etiquetas, notas, casos de postventa).",
      "Conexiones: los tokens de WhatsApp, la clave de IA propia de la empresa y el token de HubSpot, si los hay. Se guardan cifrados y nunca se vuelven a mostrar completos.",
      "Registros de funcionamiento: cuántos mensajes respondió Lumi, cuánto tardó y cuánto costó; métricas técnicas de uso que no incluyen contenido; y la auditoría de cambios de cada empresa, que registra quién cambió qué y cuándo, sin los valores.",
    ],
  },
  {
    title: "Para qué los usamos",
    items: [
      "Dar el servicio: mostrar la bandeja, enviar y recibir mensajes, y que Lumi responda.",
      "Cuidar las cuentas: verificación en dos pasos, bloqueo ante intentos fallidos y avisos por correo.",
      "Administrar los planes y los mensajes de Lumi de cada empresa.",
      "Dar soporte cuando la empresa lo pide.",
      "Vigilar que el servicio funcione (errores, tiempos de respuesta, envíos fallidos), sin leer el contenido.",
    ],
    after: [
      "No vendemos datos. No los usamos para publicidad. No usamos las conversaciones de ninguna empresa para entrenar modelos de inteligencia artificial.",
    ],
  },
  {
    title: "Lumi y la inteligencia artificial",
    paragraphs: [
      "Para responder a un cliente, Orbis envía al modelo de inteligencia artificial la conversación reciente, la configuración de Lumi de la empresa y los fragmentos de su catálogo y su conocimiento que vienen al caso. Hoy ese modelo es Claude, de Anthropic, que según sus condiciones para empresas no usa estos datos para entrenar sus modelos.",
      "La búsqueda dentro del catálogo y del conocimiento, y algunas sugerencias del panel, usan modelos que corren dentro de Cloudflare.",
      "Cada empresa puede apagar Lumi en cualquier momento; entonces el equipo contesta todos los mensajes.",
    ],
  },
  {
    title: "Con quién los compartimos",
    paragraphs: ["Solo con los proveedores que hacen posible el servicio, y solo lo necesario para cada uno:"],
    items: [
      "Cloudflare: aloja Orbis, sus bases de datos y archivos, envía los correos y corre los modelos de búsqueda.",
      "Meta (WhatsApp Business Platform): los mensajes de WhatsApp pasan por Meta, como en cualquier cuenta de WhatsApp Business.",
      "Anthropic: recibe lo que Lumi necesita para responder, como se explica arriba.",
      "HubSpot: solo si la empresa conecta su cuenta. Orbis le envía contactos, empresas, negocios, casos y un resumen de cada conversación cerrada, nunca el texto de los mensajes.",
    ],
    after: ["También compartiríamos datos si una autoridad competente lo exige conforme a la ley. No hay otros casos."],
  },
  {
    title: "Dónde están",
    paragraphs: [
      "Los datos se guardan en la infraestructura de Cloudflare, que tiene centros de datos en varios países, y pasan por los servicios de Meta y Anthropic para lo descrito arriba. Al usar Orbis aceptas que los datos se transfieran a esos proveedores fuera de Costa Rica, que los protegen con sus propias medidas de seguridad.",
    ],
  },
  {
    title: "Cómo los protegemos",
    items: [
      "Todo viaja cifrado (HTTPS). Los tokens, claves y secretos de cada empresa se guardan cifrados.",
      "Cada empresa tiene su propia base de datos, separada de las demás.",
      "La verificación en dos pasos es obligatoria para entrar al panel, hay bloqueo ante intentos fallidos y avisamos por correo de cada inicio de sesión.",
      "El equipo de Orbis no ve conversaciones, contactos ni casos de ninguna empresa sin permiso. El propietario puede dar ese permiso por un tiempo (24 horas, 3 días o 7 días) cuando pide ayuda, puede quitarlo cuando quiera, y ve cada cosa que miró el soporte y quién la miró.",
      "Las personas del equipo de Orbis entran a su panel interno con su propia cuenta y segundo factor, y cada acción queda registrada.",
    ],
  },
  {
    title: "Cuánto tiempo los guardamos",
    items: [
      "Mientras la empresa use Orbis. La empresa puede borrar casos, cotizaciones, productos y documentos desde el panel; para borrar contactos o conversaciones, nos escribe y lo hacemos.",
      "La auditoría de cambios se guarda un año.",
      "Si la empresa deja Orbis, guardamos sus datos 60 días para que pueda pedir una copia o volver. Después los borramos; las copias de respaldo se eliminan en los 30 días siguientes.",
      "Los registros de seguridad (como los intentos de inicio de sesión) se guardan solo el tiempo necesario para proteger las cuentas.",
    ],
  },
  {
    title: "Tus derechos",
    paragraphs: [
      `Conforme a la Ley 8968 de Protección de la Persona frente al Tratamiento de sus Datos Personales, puedes pedir acceso a tus datos, corregirlos, borrarlos u oponerte a que se traten. Escríbenos a ${PRIVACY_EMAIL} desde el correo de tu cuenta, o indicando cómo confirmar que eres tú, y te respondemos dentro de los plazos de la ley.`,
      "Si crees que no atendimos bien tu pedido, puedes acudir a la Agencia de Protección de Datos de los Habitantes (Prodhab).",
    ],
  },
  {
    title: "Cookies",
    paragraphs: [
      "Orbis usa solo la cookie necesaria para mantener tu sesión abierta, y guarda en tu navegador algunas preferencias del panel, como el tema claro u oscuro, los avisos y el menú contraído. No usamos cookies de publicidad ni de rastreo de terceros.",
    ],
  },
  {
    title: "Menores de edad",
    paragraphs: [
      "Orbis es un servicio para empresas. Las cuentas del panel son para personas mayores de edad que trabajan en ellas.",
    ],
  },
  {
    title: "Cambios a esta política",
    paragraphs: [
      "Si cambiamos algo importante, avisamos por correo a los propietarios de cada empresa antes de que el cambio aplique. La fecha de arriba indica la última actualización.",
    ],
  },
];

export const TERMS: LegalSection[] = [
  {
    title: "El servicio",
    paragraphs: [
      "Orbis es un servicio en línea para que las empresas atiendan su WhatsApp: bandeja compartida para el equipo, contactos, postventa, cotizaciones, reportes y Lumi, una inteligencia artificial que responde con la información que carga cada empresa. Al crear una cuenta o usar Orbis aceptas estos términos y la política de privacidad.",
      "Quien crea la cuenta de una empresa declara que puede representarla y aceptar estos términos en su nombre.",
    ],
  },
  {
    title: "Cuentas",
    items: [
      "Cada persona usa su propia cuenta: no se comparten contraseñas ni códigos.",
      "La verificación en dos pasos es obligatoria. Si alguien pierde su código, el equipo de Orbis puede quitarlo después de confirmar que es esa persona.",
      "El propietario y los administradores deciden quién entra al equipo de la empresa y con qué rol, y responden por el uso que hace su equipo.",
    ],
  },
  {
    title: "Planes y pagos",
    items: [
      "Los planes se pagan por año, en dólares, y se muestran por mes. El precio, la cantidad de usuarios y los mensajes de Lumi de cada plan son los publicados por Orbis o los acordados con la empresa.",
      "Cada mensaje que Lumi envía a un cliente cuenta como uno del plan. Los mensajes del mes no se acumulan; las recargas valen hasta que termina el año contratado.",
      "Lo que Meta cobra por las conversaciones de WhatsApp lo paga la empresa directamente a Meta.",
      "Si una empresa empieza con una prueba gratis, dura 14 días; al terminar elige un plan y conserva lo que configuró.",
      "Si no quedas satisfecho, puedes pedir la devolución completa dentro de los primeros 30 días de tu primer pago.",
      "Si un pago no se completa, avisamos y damos un plazo antes de suspender la cuenta.",
    ],
  },
  {
    title: "Uso permitido",
    paragraphs: ["Al usar Orbis te comprometes a:"],
    items: [
      "Cumplir las políticas de WhatsApp Business y de Meta: escribir solo a quien aceptó recibir mensajes de tu empresa y usar plantillas aprobadas cuando corresponda.",
      "No enviar spam, contenido ilegal, engañoso u ofensivo, ni usar Orbis para acosar a nadie.",
      "No intentar entrar a datos de otras empresas, saltarse los límites del servicio ni afectar su funcionamiento.",
      "No revender ni dar acceso a Orbis a terceros sin acuerdo con nosotros.",
    ],
    after: [
      "Podemos suspender una cuenta que incumpla estas reglas, avisando primero salvo que haya un riesgo urgente.",
    ],
  },
  {
    title: "Lumi",
    paragraphs: [
      "Lumi responde con la información y la configuración que carga tu empresa, y pasa la conversación a una persona cuando hace falta. Es una inteligencia artificial: puede equivocarse. Revisa su configuración y su conocimiento, y prueba sus respuestas antes de encenderla. Tu empresa responde ante sus clientes por la información que publica a través de Lumi.",
    ],
  },
  {
    title: "Tus datos",
    paragraphs: [
      "Los datos que tu empresa carga o recibe en Orbis son suyos. Orbis los trata solo para darte el servicio, como explica la política de privacidad. Puedes pedirnos una copia en cualquier momento.",
      "Si dejas Orbis, guardamos tus datos 60 días para que puedas pedir una copia o volver; después los borramos.",
    ],
  },
  {
    title: "Soporte",
    paragraphs: [
      "Te ayudamos a configurar y a resolver problemas. Para mirar las conversaciones, contactos o casos de tu empresa, el equipo de Orbis necesita que el propietario le dé acceso desde Ajustes, por un tiempo y con registro de todo lo que mira.",
    ],
  },
  {
    title: "Disponibilidad",
    paragraphs: [
      "Hacemos lo posible para que Orbis funcione siempre, pero no podemos garantizar que nunca falle. Orbis depende de servicios de terceros (Meta, Cloudflare, Anthropic) que también pueden tener interrupciones. Avisamos con anticipación de los mantenimientos que afecten el uso.",
    ],
  },
  {
    title: "Responsabilidad",
    paragraphs: [
      "En la medida en que la ley lo permita, la responsabilidad de Orbis por cualquier reclamo relacionado con el servicio se limita a lo que tu empresa pagó por Orbis en los 12 meses anteriores, y no incluye daños indirectos como ganancias o ventas perdidas.",
    ],
  },
  {
    title: "Cancelación",
    paragraphs: [
      "Puedes dejar de usar Orbis cuando quieras. Salvo la devolución de los primeros 30 días, no hay reembolsos por el tiempo que quede del año pagado. Orbis puede terminar el servicio a una empresa que incumpla estos términos, avisando primero.",
    ],
  },
  {
    title: "Cambios a estos términos",
    paragraphs: [
      "Si cambiamos estos términos, avisamos por correo a los propietarios al menos 30 días antes de que apliquen. Si no estás de acuerdo, puedes cancelar antes de esa fecha.",
    ],
  },
  {
    title: "Ley aplicable y contacto",
    paragraphs: [
      "Estos términos se rigen por las leyes de la República de Costa Rica. Cualquier diferencia que no se resuelva conversando se lleva ante los tribunales de San José.",
      `Para temas comerciales, escríbenos a ${SALES_EMAIL}; para privacidad, a ${PRIVACY_EMAIL}.`,
    ],
  },
];
